import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Droplets, Flame, Gauge, ArrowRight, CheckCircle, Phone } from 'lucide-react';

const PHONE = "403-615-8770";

const services = [
  {
    id: 'plumbing',
    name: 'Plumbing',
    icon: Droplets,
    color: '#0EA5E9',
    description: 'Complete residential plumbing services from emergency repairs to full installations. Our journeyman plumbers handle everything from simple fixes to complex renovations.',
    path: '/services/plumbing',
    services: [
      'Poly-B Replacement',
      'Tankless Water Heater Installation & Service',
      'Hot Water Tank Replacement & Repairs',
      'Drain Cleaning',
      'Leak Repairs',
      'Bathroom & Kitchen Plumbing',
      'Water Pressure Issues',
      'Renovation & Custom Home Plumbing',
      'Rough-ins'
    ]
  },
  {
    id: 'heating',
    name: 'Heating',
    icon: Flame,
    color: '#F97316',
    description: 'Furnace, boilers, hydronic, and in-floor radiant heating. Keep your home or business warm and comfortable through Calgary winters.',
    path: '/services/heating',
    services: [
      'Furnace Installation',
      'Furnace Installation & Repair',
      'Boiler & Hydronic Systems',
      'In-Floor Radiant Heating',
      'Garage Heaters',
      'Thermostats'
    ]
  },
  {
    id: 'gas-fitting',
    name: 'Gas Fitting',
    icon: Gauge,
    color: '#0284C7',
    description: 'Licensed gas fitting services for safe, code-compliant installations. From BBQ lines to garage heater connections.',
    path: '/services/gas-fitting',
    services: [
      'Gas Line Installation',
      'Gas Safety & Testing',
      'BBQ Gas Lines',
      'Garage Heater Gas Lines',
      'Gas Appliance Hookups',
      'Gas Fireplace Connections'
    ]
  }
];

export const ServicesPage = () => {
  return (
    <main data-testid="services-page" className="page-transition">
      {/* Hero Section */}
      <section className="pt-32 pb-16 section-grey">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="text-[#0F172A] uppercase tracking-widest text-sm font-semibold">Our Services</span>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl text-[#0F172A] mt-4 mb-6">
              Plumbing, Heating <span className="text-[#0284C7]">&amp; Gas Fitting</span>
            </h1>
            <p className="text-slate-600 max-w-2xl mx-auto text-lg">
              Expert plumbing, heating, and gas fitting services for your home or light commercial property in Calgary. Quality workmanship backed by journeyman certification and 25+ years of experience.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 section-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`service-section-${service.id}`}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-4 mb-6">
                    <div 
                      className="icon-container"
                      style={{ background: `linear-gradient(135deg, ${service.color}15, ${service.color}05)` }}
                    >
                      <service.icon className="w-8 h-8" style={{ color: service.color }} strokeWidth={1.5} />
                    </div>
                    <h2 className="font-heading font-bold text-3xl text-[#0F172A]">
                      {service.name}
                    </h2>
                  </div>
                  
                  <p className="text-slate-600 mb-8 text-lg">
                    {service.description}
                  </p>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <Link
                      to={service.path}
                      data-testid={`service-link-${service.id}`}
                      className="btn-primary rounded-lg inline-flex items-center justify-center gap-2"
                    >
                      Learn More
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link
                      to="/contact"
                      className="btn-secondary rounded-lg inline-flex items-center justify-center"
                    >
                      Book Service
                    </Link>
                  </div>
                </div>

                {/* Services List */}
                <div className={`bg-white p-8 rounded-2xl shadow-lg border border-slate-200 ${
                  index % 2 === 1 ? 'lg:order-1' : ''
                }`}>
                  <h3 className="font-heading font-bold text-xl text-[#0F172A] mb-6">
                    {service.name} Services Include:
                  </h3>
                  <ul className="space-y-3">
                    {service.services.map((item) => (
                      <li key={item} className="flex items-center gap-3 text-slate-600">
                        <CheckCircle className="w-5 h-5 text-[#0F172A] flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 cta-banner">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-white/90 mb-6">
            Book your service today or call for a free estimate.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-[#0F172A] font-bold uppercase tracking-wider px-8 py-4 rounded-lg hover:bg-slate-50 transition-colors inline-flex items-center justify-center gap-2"
            >
              Book Service
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href={`tel:${PHONE}`}
              className="border-2 border-white text-white font-bold uppercase tracking-wider px-8 py-4 rounded-lg hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Call For Free Estimate
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ServicesPage;
